/**
 * Iter 231 — Connect pillar hub: /portal-admin
 *
 * Admin/owner/manager dashboard for managing customer-portal access.
 * Shows which customers have portal users + counts + last-login summary.
 *
 * Lifts portal-admin surfaces out of /settings/platform into a real
 * Connect-pillar page. No modal usage (Trust Contract).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ArrowUpRight, Search, ShieldCheck, KeyRound,
  Loader2, Phone,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useBranding } from "../context/TenantBrandingContext";
import { EntityShell } from "../components/shell";
import { useAuth } from "../context/AuthContext";

const PRESET_LABELS = {
  owner: "Owner",
  admin: "Admin",
  accounts: "Accounts",
  maintenance: "Maintenance",
  custom: "Custom",
};

const PRESET_COLORS = {
  owner: "#1FB6C1",
  admin: "#A78BFA",
  accounts: "#F59E0B",
  maintenance: "#10B981",
  custom: "#647087",
};

export default function PortalAdminHub() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const BRAND = branding?.app_name || "Portal";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/portal-admin/overview");
      setRows(data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load portal overview"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const isAdmin = ["owner", "admin", "manager"].includes(user?.role);
  if (!isAdmin) {
    return (
      <EntityShell kindLabel="Connect" title="Portal admin">
        <div className="hv-card p-5 text-sm text-[#a0aabf]" data-testid="portal-admin-denied">
          You need admin or manager access to view this page.
        </div>
      </EntityShell>
    );
  }

  const filteredRows = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.external_ref || "").toLowerCase().includes(s)
    );
  });

  const totalUsers = rows.reduce((acc, r) => acc + (r.user_count || 0), 0);
  const totalCustomers = rows.length;

  return (
    <EntityShell
      kindLabel="Connect"
      title={`${BRAND} portal admin`}
      backTo="/"
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/settings/platform#modules"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="portal-admin-modules-link"
          >
            Module settings <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      }
    >
      <div className="space-y-5" data-testid="portal-admin-hub">
        <p className="text-sm text-[#a0aabf] max-w-2xl">
          Who at your customers can sign into the {BRAND} portal? Each customer can have
          their own team (owner / admin / accounts / maintenance / custom presets) and
          you can audit how active they are here.
        </p>

        {/* KPIs */}
        <div className="grid sm:grid-cols-3 gap-3" data-testid="portal-admin-kpis">
          <Kpi
            label="Customers with portal"
            value={totalCustomers}
            color="#1FB6C1"
            icon={ShieldCheck}
            testId="portal-admin-kpi-customers"
          />
          <Kpi
            label="Total portal users"
            value={totalUsers}
            color="#A78BFA"
            icon={Users}
            testId="portal-admin-kpi-users"
          />
          <Kpi
            label="Avg users / customer"
            value={totalCustomers ? (totalUsers / totalCustomers).toFixed(1) : "0.0"}
            color="#10B981"
            icon={KeyRound}
            testId="portal-admin-kpi-avg"
          />
        </div>

        {/* Search + reload */}
        <div className="hv-card p-3 flex items-center gap-2 flex-wrap">
          <Search className="w-4 h-4 text-[#647087]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer name, email, or external ID"
            className="hv-input flex-1 min-w-[200px]"
            data-testid="portal-admin-search"
          />
          <button
            onClick={refresh}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="portal-admin-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </button>
        </div>

        {err && (
          <div className="hv-card p-3 text-sm text-rose-200 border border-rose-500/40 bg-rose-500/5" data-testid="portal-admin-err">
            {err}
          </div>
        )}

        {/* Customer-with-portal list */}
        {loading ? (
          <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="portal-admin-loading">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading portal customers…
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="portal-admin-empty">
            {search ? (
              <>No matches for "<strong>{search}</strong>".</>
            ) : (
              <>
                No customer portals yet. Open any customer's profile and click
                <em className="text-[#1fb6c1] not-italic"> Invite to portal </em> to start.
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="portal-admin-list">
            {filteredRows.map((r) => (
              <PortalCustomerRow key={r.customer_id} row={r} />
            ))}
          </div>
        )}
      </div>
    </EntityShell>
  );
}

/* ─── Sub-components ─── */
function Kpi({ label, value, color, icon: Icon, testId }) {
  return (
    <div
      className="hv-card p-4 flex items-center justify-between"
      style={{ borderColor: `${color}40`, background: `${color}08` }}
      data-testid={testId}
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">{label}</div>
        <div className="font-display text-3xl font-bold mt-0.5" style={{ color }}>
          {value}
        </div>
      </div>
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center"
        style={{ background: `${color}1a`, color, border: `1px solid ${color}55` }}
      >
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

function PortalCustomerRow({ row }) {
  const presets = row.preset_counts || {};
  const presetEntries = Object.entries(presets).sort((a, b) => b[1] - a[1]);
  return (
    <div
      className="hv-card p-4 flex items-center gap-4 flex-wrap"
      data-testid={`portal-admin-row-${row.customer_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate flex items-center gap-2">
          <Link
            to={`/customers/${row.customer_id}`}
            className="hover:underline"
            data-testid={`portal-admin-row-link-${row.customer_id}`}
          >
            {row.name}
          </Link>
          {row.external_ref && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/10 text-[#647087]">
              {row.external_ref}
            </span>
          )}
        </div>
        {row.email && (
          <div className="text-xs text-[#647087] truncate flex items-center gap-1 mt-0.5">
            <Phone className="w-3 h-3" /> {row.email}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {presetEntries.map(([preset, count]) => (
            <span
              key={preset}
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border inline-flex items-center gap-1"
              style={{
                borderColor: `${PRESET_COLORS[preset] || "#647087"}55`,
                background: `${PRESET_COLORS[preset] || "#647087"}11`,
                color: PRESET_COLORS[preset] || "#a0aabf",
              }}
            >
              {PRESET_LABELS[preset] || preset} · {count}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Users</div>
          <div className="font-display text-2xl font-bold text-[#1fb6c1]">
            {row.user_count}
          </div>
        </div>
        <Link
          to={`/customers/${row.customer_id}#portal-access`}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`portal-admin-manage-${row.customer_id}`}
        >
          Manage <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
